<template>
  <!--
    Context-window meter. Renders the size of the prompt the API processed
    on the last turn against the model's window, so we can confirm
    server-side context editing (clear_tool_uses) is holding the
    conversation below the limit. The bar shifts indigo → amber → red as it
    fills; a ✦ badge appears on turns where stale tool results were cleared.
    Renders nothing until usage telemetry is available.
  -->
  <div v-if="meter" class="ctx-meter" :title="title">
    <div class="ctx-meter-bar">
      <div
        class="ctx-meter-fill"
        :class="meter.level"
        :style="{ width: meter.pct + '%' }"
      />
    </div>
    <span class="ctx-meter-label">
      {{ formatTokens(meter.used) }} / {{ formatTokens(meter.window) }}
    </span>
    <span
      v-if="meter.cleared > 0"
      class="ctx-meter-cleared"
      title="Stale tool results cleared this turn by context editing"
    >✦ {{ formatTokens(meter.cleared) }}</span>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';

export default defineComponent({
  name: 'ContextMeter',
  props: {
    /**
     * ChatResponse.context_usage shape (or null):
     *   { input_tokens, cache_read_input_tokens, cache_creation_input_tokens,
     *     total_tokens, context_window, cleared_input_tokens }
     */
    usage: { type: Object, default: null },
  },
  setup(props) {
    /** Compact token formatter: 142345 → "142.3k", 980 → "980". */
    function formatTokens(n) {
      const v = Number(n) || 0;
      if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
      return String(v);
    }

    const meter = computed(() => {
      const u = props.usage;
      if (!u || !u.context_window) return null;
      const pct = Math.min(
        100,
        Math.round((u.total_tokens / u.context_window) * 100),
      );
      const level = pct >= 90 ? 'is-danger' : pct >= 70 ? 'is-warn' : 'is-ok';
      return {
        used: u.total_tokens,
        window: u.context_window,
        pct,
        level,
        cleared: u.cleared_input_tokens || 0,
      };
    });

    const title = computed(() => {
      const m = meter.value;
      if (!m) return '';
      const base =
        `Context used last turn: ${m.used.toLocaleString()} / ` +
        `${m.window.toLocaleString()} tokens (${m.pct}%)`;
      return m.cleared > 0
        ? `${base}\nContext editing cleared ${m.cleared.toLocaleString()} tokens of stale tool results this turn.`
        : base;
    });

    return { meter, title, formatTokens };
  },
});
</script>

<style scoped>
.ctx-meter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 9999px;
  background: rgb(243 244 246);
  cursor: default;
  user-select: none;
}
.ctx-meter-bar {
  width: 64px;
  height: 6px;
  border-radius: 9999px;
  background: rgb(229 231 235);
  overflow: hidden;
}
.ctx-meter-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.4s ease, background-color 0.4s ease;
}
.ctx-meter-fill.is-ok {
  background: rgb(79 70 229);
}
.ctx-meter-fill.is-warn {
  background: rgb(217 119 6);
}
.ctx-meter-fill.is-danger {
  background: rgb(220 38 38);
}
.ctx-meter-label {
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: rgb(75 85 99);
  white-space: nowrap;
}
.ctx-meter-cleared {
  font-size: 11px;
  font-weight: 600;
  color: rgb(5 150 105);
  white-space: nowrap;
}
</style>
